/* eslint-disable no-useless-escape */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { selectChatState, selectUser } from '../../../../store/app.selectors';

import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { getTagValue, pickProfileImage, resolveOwner } from '../../../../core/utils/owner-match';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { ImagesService } from '../../../shared/services/images.service';
import { ChannelType } from '../../interfaces/IChannel';
import { IMessage, IReactionGroup } from '../../interfaces/IMessage';
import { DinamicLinksURL, RepairOrderUrl, WorkspaceFeatures } from '../../interfaces/IWorkspace';
import { MessagesService } from '../../services/messages.service';
import { MessagingApiService } from '../../services/messaging-api/messaging-api.service';
import { PermissionsService } from '../../services/permissions.service';
import { TranslaterService } from '../../services/translater.service';
import { deleteMessagesById, resendMessage, setMessageDeliveryStatus, setMessageReaction } from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';
import { EmojiModalComponent } from '../emoji/emoji.component';

@Component({
  selector: 'ngx-message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MessageComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() message: IMessage;
  @Output() linkClicked = new EventEmitter<boolean>();

  private messagingApiService = inject(MessagingApiService);
  private store = inject(Store<AppStateWithChat>);
  public translaterService = inject(TranslaterService);
  public permissionsService = inject(PermissionsService);
  private env = inject(MessagingEnvironmentService);
  public dialog = inject(MatDialog);
  public imagesService = inject(ImagesService);
  public messagesService = inject(MessagesService);

  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });
  user = toSignal(this.store.select(selectUser), { initialValue: null });

  selectedChannel = computed(() => this.chat().selectedChannel);
  selectedWorkspace = computed(() => this.chat().selectedWorkSpace);
  canDisplayAuthor = computed(() => {
    const channel = this.selectedChannel();
    return !!(
      this.message.autor &&
      ((this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.showOwnerMessages]) && this.message.outcoming) ||
        channel?.type !== ChannelType.DIRECT_MESSAGE)
    );
  });

  resolvedAuthor() {
    const autor = this.message?.autor;
    const ownerId = getTagValue(this.message?.systemTags, 'ownerId') || autor?.id;
    const channel = this.selectedChannel();
    return resolveOwner(ownerId, autor, {
      users: this.chat().users,
      extras: [channel?.owner, this.user()?.subjectMessageableEntity, this.message?.createdBy, ...(channel?.members ?? [])],
      tagDisplayName: getTagValue(this.message?.systemTags, 'ownerDisplayName'),
    });
  }

  authorDisplayName(): string {
    return this.resolvedAuthor()?.displayName || this.message?.autor?.displayName || '';
  }

  authorColorSeed(): string {
    const author = this.resolvedAuthor();
    return (
      author?.id || author?.uid || author?.displayName || this.message?.autor?.id || this.message?.autor?.displayName || ''
    );
  }

  authorAvatarSrc(): string | undefined {
    const resolved = this.resolvedAuthor();
    const me = this.user()?.subjectMessageableEntity;
    const createdBy = this.message?.createdBy;
    const channel = this.selectedChannel();
    const member = channel?.members?.find(
      (item) => item.uid === createdBy?.uid || item.businessReference?.entityUID === resolved?.id
    );
    const mine =
      this.message?.outcoming &&
      me &&
      (me.businessReference?.entityUID === resolved?.id ||
        me.uid === resolved?.id ||
        me.displayName?.trim().toLowerCase() === resolved?.displayName?.trim().toLowerCase());

    return (
      resolved?.profileImageURL ||
      this.message?.autor?.profileImageURL ||
      pickProfileImage(member) ||
      pickProfileImage(createdBy) ||
      (mine ? pickProfileImage(me) : undefined)
    );
  }

  body: string;
  hide: boolean;
  videoCallRoom: string;
  hideTranslation = false;
  translation: string;
  reactionsGroup: IReactionGroup[] = [] as IReactionGroup[];
  deliveryStatus: string;

  messageElementRef = viewChild<ElementRef<Element>>('messageElement');
  private observer: IntersectionObserver;

  ngOnInit() {
    this.checkDynamicLinks();
  }

  ngOnChanges(): void {
    this.checkDynamicLinks();
    this.setReactions();
    this.deliveryStatus = this.getDeliveryStatusIcon();
  }

  ngAfterViewInit() {
    const element = this.messageElementRef();
    if (element) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.messagesService.clearUnreadMessages(this.message);
          }
        },
        { threshold: 0.1 }
      );
      this.observer.observe(element.nativeElement);
    }
  }

  ngOnDestroy() {
    const element = this.messageElementRef();
    if (this.observer && element) {
      this.observer.unobserve(element.nativeElement);
    }
    super.ngOnDestroy?.();
  }

  checkDynamicLinks(): void {
    this.body = this.linkify(this.message.body);
    this.videoCallRoom = this.getPatternValue(this.message?.body ?? '', /vc=+([a-zA-Z0-9\-_]+)/i);
    this.hide = Boolean(this.videoCallRoom);
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canSeeHyperlinks])) {
      for (const iterator of this.selectedWorkspace()?.config?.dynamicLinks ?? []) {
        const patternValue = this.getPatternValue(this.message.body, iterator.pattern);
        if (patternValue) {
          if (iterator.previewTemplate === 'repairOrdersPreviewComponent') {
            this.messagingApiService.getRepairOrderByJobName(patternValue).subscribe((repairOrder) => {
              const pattern = new RegExp(iterator.pattern, 'i');
              this.body = this.message.body.replace(pattern, (data) => {
                return `<a href='${this.getUrlLink(iterator.url)!}${
                  repairOrder.orderId
                }' target='_blank'> <span style="background: white;
                   color: black;
                   padding: 2.5px;
                   border-radius: 5px;">${data}</span></a>`;
              });
              if (this.message?.translation) {
                this.translation = this.message.translation.replace(pattern, (data) => {
                  return `<a href='${this.getUrlLink(iterator.url)!}${
                    repairOrder.orderId
                  }' target='_blank'> <span style="background: white;
                   color: black;
                   padding: 2.5px;
                   border-radius: 5px;">${data}</span></a>`;
                });
              }
            });
          }
        }
      }
    }
  }

  setReactions(): void {
    this.reactionsGroup = [];
    const myUID = this.user()?.subjectMessageableEntity?.uid;
    if (this.message?.reactions?.length) {
      for (const reaction of this.message.reactions) {
        const mine = !!myUID && reaction.createdBy?.uid === myUID;
        const index = this.reactionsGroup.map((e) => e.body).indexOf(reaction.body);
        if (index === -1) {
          this.reactionsGroup.push({
            body: reaction.body,
            reactions: [reaction],
            tooltip: reaction.createdBy?.displayName,
            mine,
          });
        } else {
          this.reactionsGroup[index].reactions.push(reaction);
          this.reactionsGroup[index].tooltip = this.reactionsGroup[index].tooltip + ', ' + reaction.createdBy?.displayName;
          this.reactionsGroup[index].mine = this.reactionsGroup[index].mine || mine;
        }
      }
    }
  }

  onBodyClick(event: MouseEvent) {
    if (event.target instanceof HTMLAnchorElement) {
      if (this.message.body.toLowerCase().includes('video')) {
        this.linkClicked.emit(true);
      }
    }
  }

  getPatternValue(value: string, pattern: RegExp): string {
    const regexp = new RegExp(pattern);
    return regexp.test(value) ? value.match(regexp)?.[1] ?? '' : '';
  }

  getHyperlinkRepairOrder(value: string, url: string, pattern: RegExp): string {
    return value && value.includes(url) ? this.getPatternValue(value.replace(url, '#'), pattern) : '';
  }

  getRoomName(value: string, pattern: RegExp): string {
    return this.getPatternValue(value, pattern);
  }

  linkify(inputText: string) {
    let replacedText = inputText;

    if (inputText) {
      //URLs starting with http://, https://, or ftp://
      const replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
      replacedText = inputText.replace(replacePattern1, '<a href="$1" target="_blank">$1</a>');

      //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
      const replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
      replacedText = replacedText.replace(replacePattern2, '$1<a href="http://$2" target="_blank">$2</a>');

      //Change email addresses to mailto:: links.
      const replacePattern3 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
      replacedText = replacedText.replace(replacePattern3, '<a href="mailto:$1">$1</a>');
    }

    return replacedText;
  }

  resendMessage(): void {
    this.store.dispatch(resendMessage({ message: this.message }));
  }

  getDeliveryStatusIcon() {
    if (this.message?.deliveryStatus?.sending) {
      return 'access_time';
    } else if (this.message?.deliveryStatus?.anyRead || this.message?.deliveryStatus?.anyDelivered) {
      return 'check_all';
    } else if (this.message?.deliveryStatus?.anyFailed) {
      return 'error';
    } else if (this.message?.deliveryStatus?.anySent) {
      return 'check';
    } else {
      return 'check';
    }
  }

  deleteMessage(message: IMessage) {
    this.store.dispatch(
      setMessageDeliveryStatus({ channelUID: this.selectedChannel()!.uid, messageUID: message.uid, status: 'sending' })
    );
    this.store.dispatch(deleteMessagesById({ messageUid: message.uid }));
  }

  getUrlLink(link: DinamicLinksURL | null): RepairOrderUrl {
    return this.env.env[link!];
  }

  translateMessage(): void {
    this.translaterService.translateMessages(this.selectedChannel()!.messages, this.selectedChannel()!);
  }

  openReactionSelector(): void {
    this.dialog
      .open(EmojiModalComponent, {
        panelClass: 'custom-emoji-modal',
      })
      .afterClosed()
      .subscribe((res: string) => {
        if (res) {
          this.store.dispatch(
            setMessageReaction({
              messageUID: this.message.uid,
              channelUID: this.selectedChannel()!.uid,
              reaction: { body: res, createdBy: this.user()!.subjectMessageableEntity! },
            })
          );
        }
      });
  }
}
